import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTrackApplication } from '../../hooks/useCandidates';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import StageBadge from '../../components/ui/StageBadge';
import Badge from '../../components/ui/Badge';
import StageTimeline from '../../components/StageTimeline';
import BrandBadge from '../../components/brand/BrandBadge';
import { formatDate, formatPercent } from '../../utils/formatters';
import { formatTrackLookupError } from '../../utils/candidateAuthErrors';
import { MCQ_ASSESSMENT_TYPES } from '../../utils/constants';

export default function TrackApplication() {
  const [trackParams, setTrackParams] = useState({ code: '', email: '' });
  const { register, handleSubmit, formState: { errors } } = useForm();

  const { data, isLoading, isError, error, isFetching } = useTrackApplication(
    trackParams.code,
    trackParams.email
  );

  const onSubmit = (formData) => {
    setTrackParams({
      code: formData.code.trim().toUpperCase(),
      email: formData.email.trim(),
    });
  };

  const candidate = data?.candidate;
  const assessments = (data?.assessments || []).filter(a => MCQ_ASSESSMENT_TYPES.includes(a.test_type));
  const stageHistory = data?.stageHistory || [];
  const lookupError = isError ? formatTrackLookupError(error) : null;

  return (
    <div className="min-h-screen bg-slate-50 py-8 sm:py-10 px-4" data-testid="track-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <BrandBadge />
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-4">Track Your Application</h1>
          <p className="text-slate-500 mt-1.5 text-sm">
            Enter the HIQ code from your confirmation email and the same email you applied with.
          </p>
        </div>

        <Card className="mb-4">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3"
            data-testid="track-form"
          >
            <Input
              label="Application Code"
              placeholder="HIQ-XXXXXX"
              className="w-full sm:flex-1"
              autoComplete="off"
              data-testid="track-code"
              error={errors.code?.message}
              {...register('code', {
                required: 'Application code is required',
                pattern: {
                  value: /^HIQ-[A-Z0-9]{6}$/i,
                  message: 'Use the format HIQ-XXXXXX from your confirmation email',
                },
              })}
            />
            <Input
              label="Email"
              type="email"
              placeholder="you@email.com"
              className="w-full sm:flex-1"
              autoComplete="email"
              data-testid="track-email"
              error={errors.email?.message}
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
            />
            <Button type="submit" className="w-full sm:w-auto" loading={isLoading || isFetching} data-testid="track-submit">
              <MagnifyingGlassIcon className="h-4 w-4" /> Track
            </Button>
          </form>
        </Card>

        {lookupError && (
          <div
            role="alert"
            data-testid="track-error"
            className="mb-6 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3.5 text-left"
          >
            <p className="text-sm font-semibold text-danger-800">We couldn&apos;t find that application</p>
            <p className="mt-1 text-sm text-danger-700">{lookupError}</p>
            <ul className="mt-3 space-y-1 text-sm text-danger-700 list-disc list-inside">
              <li>Code looks like <span className="font-mono">HIQ-</span> plus six letters or numbers</li>
              <li>Email must match the one on your original application (typos count)</li>
              <li>New applications can take a minute to appear — try again shortly</li>
            </ul>
            <p className="mt-3 text-sm text-danger-700">
              Still stuck?{' '}
              <Link to="/apply" className="font-medium underline hover:text-danger-900">
                Apply again
              </Link>
              {' '}or{' '}
              <Link to="/candidate/signup" className="font-medium underline hover:text-danger-900">
                create a candidate account
              </Link>
              .
            </p>
          </div>
        )}

        {candidate && (
          <div className="space-y-6" data-testid="track-result">
            <Card>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{candidate.first_name} {candidate.last_name}</h2>
                  <p className="text-gray-500">{candidate.position} &middot; {candidate.department}</p>
                </div>
                <StageBadge stage={candidate.stage} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Applied:</span> {formatDate(candidate.applied_date)}</div>
                <div><span className="text-gray-500">App Code:</span> <span className="font-mono">{candidate.app_code}</span></div>
              </div>
            </Card>

            {assessments.length > 0 && (
              <Card title="Assessment Results">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {assessments.map(a => (
                    <div key={a.id} className="p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm font-semibold">{a.test_type}</h4>
                        <Badge color={a.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {a.passed ? 'Passed' : 'Failed'}
                        </Badge>
                      </div>
                      <p className="text-xl font-bold">{formatPercent(a.score)}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card title="Application Timeline">
              <StageTimeline history={stageHistory} />
            </Card>
          </div>
        )}

        <div className="mt-4 surface-panel p-4 text-center">
          <p className="text-sm text-slate-600">New candidate?</p>
          <Link to="/candidate/signup" className="text-sm font-medium text-brand-600 hover:underline">
            Create your candidate account
          </Link>
        </div>
      </div>
    </div>
  );
}
